/* eslint-disable camelcase */
import { useEffect, useState } from 'react';

import Logger from '/b2b/common/helpers/Logger';
import PropTypes from 'prop-types';
import theme from '/b2b/common/theme';
import { usePrevious } from '/b2b/common/helpers/Hooks';

const devRevAppId = 'don:core:dvrv-us-1:devo/1DQGKNzW33:plug_setting/1';

const waitForPlugSDK = () =>
    new Promise((resolve, reject) => {
        const maxAttempts = 50;
        let attempts = 0;

        const check = () => {
            if (window.plugSDK) {
                resolve();
                return;
            }

            if (attempts >= maxAttempts) {
                reject(new Error('plugSDK timeout'));
                return;
            }

            attempts += 1;
            setTimeout(check, 100);
        };

        check();
    });

const DevRev = props => {
    const { customerId, devRevEnabled, devRevToken } = props;

    const [plugSDKLoaded, setPlugSDKLoaded] = useState(false);

    useEffect(() => {
        if (!devRevEnabled || plugSDKLoaded) {
            return;
        }

        const style = document.createElement('style');

        style.innerHTML = `
            iframe[data-drid=PLUG_WIDGET_IFRAME] {
                top: initial !important;
                left: initial !important;
                bottom: ${theme.spacing(2)} !important;
                right: ${theme.spacing(2)} !important;
            }
        `;

        const script = document.createElement('script');

        script.src = 'https://plug-platform.devrev.ai/static/plug.js';
        script.type = 'text/javascript';

        script.onload = () =>
            waitForPlugSDK()
                .then(() => setPlugSDKLoaded(true))
                .catch(() => setPlugSDKLoaded(false));

        document.head.appendChild(style);
        document.head.appendChild(script);
    }, [devRevEnabled, plugSDKLoaded]);

    const previousCustomerId = usePrevious(customerId);

    useEffect(() => {
        const customerChanged =
            previousCustomerId !== customerId && previousCustomerId !== undefined;

        if (!customerChanged || !plugSDKLoaded) {
            return;
        }

        if (window.plugSDK && window.plugSDK.isPlugInitialized) {
            try {
                window.plugSDK.shutdown();
            } catch (error) {
                Logger.error('DevRev: error shutting down', error);
            }
        }
    }, [customerId, plugSDKLoaded, previousCustomerId]);

    useEffect(() => {
        if (!devRevToken || !plugSDKLoaded) {
            return;
        }

        try {
            if (window.plugSDK.isPlugInitialized) {
                window.plugSDK.shutdown();
            }

            window.plugSDK.init({
                app_id: devRevAppId,
                session_token: devRevToken,
                enable_default_launcher: false,
            });
        } catch (error) {
            Logger.error('DevRev: error initializing', error);
        }
    }, [devRevToken, plugSDKLoaded]);

    return null;
};

DevRev.propTypes = {
    // Null until a customer is selected (e.g. pre-login); the component handles
    // that case, so it must not be marked required.
    customerId: PropTypes.string,
    devRevEnabled: PropTypes.bool.isRequired,
    devRevToken: PropTypes.string.isRequired,
};

export default DevRev;
